import { useEffect, useState } from 'react';
import { PiCopyBold, PiDownloadSimpleBold } from 'react-icons/pi';
import V2Modal from '@v2/components/ui/V2Modal';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';

export type LabelPreviewTarget = {
    checkItemId: number;
    packageIndex: number;
    title: string;
    initialTab?: 'preview' | 'zpl';
};

type Props = {
    shipmentId: number;
    target: LabelPreviewTarget | null;
    onClose: () => void;
};

export default function ConferenceLabelPreviewModal({ shipmentId, target, onClose }: Props) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pngUrl, setPngUrl] = useState<string | null>(null);
    const [htmlPreview, setHtmlPreview] = useState<string | null>(null);
    const [zpl, setZpl] = useState<string | null>(null);
    const [tab, setTab] = useState<'preview' | 'zpl'>('preview');

    useEffect(() => {
        if (!target) {
            setError(null);
            setHtmlPreview(null);
            setZpl(null);
            setPngUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            return;
        }

        let cancelled = false;
        let objectUrl: string | null = null;

        const load = async () => {
            setLoading(true);
            setError(null);
            setHtmlPreview(null);
            setZpl(null);
            setPngUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            setTab(target.initialTab ?? 'preview');

            const base = `/shipments/${shipmentId}/conference/labels/${target.checkItemId}`;
            const params = { package: target.packageIndex, _: Date.now() };

            try {
                const zplRes = await api.get<string>(`${base}/zpl`, {
                    params,
                    responseType: 'text',
                    headers: { Accept: 'text/plain, application/octet-stream' },
                    transformResponse: [(data) => data],
                });

                if (cancelled) return;
                setZpl(typeof zplRes.data === 'string' ? zplRes.data : String(zplRes.data ?? ''));

                try {
                    const pngRes = await api.get<Blob>(`${base}/preview`, {
                        params: { ...params, format: 'png' },
                        responseType: 'blob',
                        headers: { Accept: 'image/png' },
                    });

                    if (cancelled) return;

                    if (pngRes.data.type.startsWith('image/')) {
                        objectUrl = URL.createObjectURL(pngRes.data);
                        setPngUrl(objectUrl);
                    } else {
                        throw new Error('Resposta não é imagem');
                    }
                } catch {
                    const htmlRes = await api.get<string>(`${base}/preview`, {
                        params,
                        responseType: 'text',
                        headers: { Accept: 'text/html' },
                        transformResponse: [(data) => data],
                    });

                    if (cancelled) return;
                    setHtmlPreview(
                        typeof htmlRes.data === 'string' ? htmlRes.data : String(htmlRes.data ?? ''),
                    );
                }
            } catch {
                if (!cancelled) {
                    setError('Não foi possível carregar a etiqueta. Tente novamente.');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        void load();

        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [target, shipmentId]);

    const downloadZpl = () => {
        if (!zpl || !target) return;
        const blob = new Blob([zpl], { type: 'application/octet-stream' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `etiqueta-${target.title.replace(/\s+/g, '-')}.zpl`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const copyZpl = async () => {
        if (!zpl) return;
        await navigator.clipboard.writeText(zpl);
    };

    return (
        <V2Modal
            open={target !== null}
            onClose={onClose}
            title="Etiqueta de conferência"
            description={target?.title}
            size="2xl"
            footer={
                <>
                    <button
                        type="button"
                        onClick={copyZpl}
                        disabled={!zpl}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <PiCopyBold /> Copiar ZPL
                    </button>
                    <button
                        type="button"
                        onClick={downloadZpl}
                        disabled={!zpl}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-50"
                    >
                        <PiDownloadSimpleBold /> Baixar .zpl
                    </button>
                </>
            }
        >
            <div className="mb-3 flex gap-2 border-b border-gray-100 pb-2">
                <button
                    type="button"
                    onClick={() => setTab('preview')}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                        tab === 'preview'
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    Visualização
                </button>
                <button
                    type="button"
                    onClick={() => setTab('zpl')}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                        tab === 'zpl'
                            ? 'bg-primary/10 text-primary'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    Código ZPL
                </button>
            </div>

            {loading ? <LoadingState /> : null}
            {error ? <p className="text-sm text-red-600">{error}</p> : null}

            {!loading && !error && tab === 'preview' ? (
                <div className="flex justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
                    {pngUrl ? (
                        <img
                            src={pngUrl}
                            alt="Preview etiqueta"
                            className="max-h-[420px] w-auto object-contain"
                        />
                    ) : htmlPreview ? (
                        <iframe
                            title="Preview etiqueta"
                            srcDoc={htmlPreview}
                            className="aspect-square h-auto w-full max-w-[400px] rounded border border-gray-200 bg-white"
                            sandbox=""
                        />
                    ) : (
                        <p className="text-sm text-gray-500">Sem preview disponível.</p>
                    )}
                </div>
            ) : null}

            {!loading && !error && tab === 'zpl' ? (
                <pre className="max-h-[420px] overflow-auto rounded-lg border border-gray-200 bg-gray-900 p-3 text-[11px] leading-relaxed text-emerald-100">
                    {zpl ?? '—'}
                </pre>
            ) : null}
        </V2Modal>
    );
}
